import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { articleCrumbs, breadcrumbJsonLd, getTopic } from '../articles';
import { Breadcrumb, FitCheckBox, MoreTopics, QuickLink } from '../components/articles/ArticleParts';
import { SvsVorschreibungMock } from '../components/svs-guide/SvsVorschreibungMock';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import { SvsEstimateCalculator } from '../components/svs-guide/SvsEstimateCalculator';
import '../components/svs-guide/svs-guide.css';

// Published article (src/articles.ts) — prerenderRoutes.ts emits the
// matching robots tag for the static HTML.
const INDEXABLE = true;

const TOPIC = getTopic('svs');
const ARTICLE = TOPIC.de;
const CRUMBS = articleCrumbs(TOPIC, 'de');
const PATH = ARTICLE.path;
const CANONICAL = `https://pstax.at${PATH}`;
// Exported so prerenderRoutes.ts uses the same title, description and
// Article JSON-LD as the client-side head (no second copy to drift apart).
export const TITLE_DE = ARTICLE.title;
export const DESCRIPTION_DE = ARTICLE.description;
const H1 = ARTICLE.h1;

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Steuerberater',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_DE = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: ARTICLE.datePublished,
  dateModified: ARTICLE.dateModified,
  inLanguage: 'de',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'Sozialversicherungsanstalt der Selbständigen',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'Was ist die SVS?', id: 'was' },
  { label: 'Muss ich überhaupt zahlen?', id: 'wer' },
  { label: 'Wie viel kostet die SVS 2026?', id: 'kosten' },
  { label: 'Warum das erste Jahr günstig wirkt', id: 'erste-jahre' },
  { label: 'SVS-Nachzahlung: wann sie kommt und wie du sie berechnest', id: 'nachzahlung' },
  { label: 'Wann zahlst du?', id: 'wann' },
  { label: 'Wofür zahlst du eigentlich?', id: 'leistungen' },
  { label: 'Wenn du wenig verdienst', id: 'geringes-einkommen' },
  { label: 'Zu hoch oder zu niedrig?', id: 'zu-hoch' },
  { label: 'SVS und Steuer gehören zusammen', id: 'steuer' },
  { label: 'Fünf Fehler, die wir oft sehen', id: 'fehler' },
  { label: 'Häufige Fragen', id: 'faq' },
  { label: 'Offizielle Quellen', id: 'quellen' },
];

const AT_A_GLANCE: [string, string][] = [
  ['Wer zahlt', 'Fast alle Selbständigen in Österreich'],
  ['Mindestbeitragsgrundlage', '551,10 € / Monat'],
  ['Höchstbeitragsgrundlage', '8.085,00 € / Monat'],
  ['Niedrigstmöglicher Beitrag', '160,81 € / Monat'],
  ['Zahlung', '4× im Jahr oder monatlich'],
  ['Versicherungsgrenze Neue Selbständige', '6.613,20 € / Jahr'],
  ['Der Haken', 'Die Beiträge werden Jahre später neu berechnet'],
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
// Most answers render exactly their plain text (`plainFaq`), so the visible
// answer and the JSON-LD answer can't drift apart.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_DE: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'Wie viel SVS muss ich als Selbständige:r zahlen?',
    'Rund 27 % deiner Beitragsgrundlage: 2026 sind das 18,5 % Pensionsversicherung, 6,8 % Krankenversicherung und 1,53 % Selbständigenvorsorge, dazu 12,95 € Unfallversicherung im Monat. Mindestens zahlst du 160,81 € im Monat, höchstens rund 26.200 € im Jahr. Als Faustregel mit Basis- oder Kleinunternehmerpauschalierung und Gewinnfreibetrag: Der Gewinn liegt bei etwa 70 % des Umsatzes, die SVS damit bei rund 20 % des Umsatzes.',
  ),
  plainFaq(
    'Wie hoch ist der SVS-Mindestbeitrag 2026?',
    'Der SVS-Mindestbeitrag 2026 beträgt 160,81 € im Monat, also 482,43 € pro Quartal oder 1.929,72 € im Jahr. Er ergibt sich aus 26,83 % der Mindestbeitragsgrundlage von 551,10 € monatlich plus 12,95 € Unfallversicherung. Weniger zahlen nur Neue Selbständige unter der Versicherungsgrenze (gar nichts) und Gewerbetreibende mit Kleinunternehmer-Ausnahme (nur die Unfallversicherung).',
  ),
  plainFaq(
    'Wie hoch ist der maximale SVS-Beitrag?',
    'Maximal zahlst du 2026 rund 26.200 € SVS im Jahr, inklusive Unfallversicherung. Die Beiträge werden höchstens von der Höchstbeitragsgrundlage berechnet, die 2026 bei 8.085 € im Monat bzw. 97.020 € im Jahr liegt. Liegt deine Beitragsgrundlage (Gewinn plus vorgeschriebene SVS-Beiträge) darüber, steigt die SVS nicht weiter.',
  ),
  plainFaq(
    'Ab wann muss ich SVS zahlen?',
    'Mit Gewerbeschein ab dem Tag, an dem deine Gewerbeberechtigung beginnt — auch wenn du noch keinen Umsatz machst; die Wirtschaftskammer meldet dich automatisch bei der SVS an. Als Neue:r Selbständige:r (ohne Gewerbeschein) bist du erst pflichtversichert, wenn dein Gewinn 2026 über 6.613,20 € liegt; rechnest du damit, musst du dich innerhalb eines Monats nach Beginn selbst bei der SVS melden. Beim Finanzamt meldest du dich in beiden Fällen zusätzlich selbst an.',
  ),
  plainFaq(
    'Wann kommt die SVS-Nachzahlung?',
    'Ohne dein Zutun meist im dritten Jahr: Der Einkommensteuerbescheid für 2026 kommt 2027, und die SVS schreibt die Nachzahlung für 2026 dann 2028 vor, verteilt auf die vier Quartale. Liegt der Steuerbescheid vor, kann man bei der SVS eine vorzeitige Endabrechnung beantragen — dann ist das Jahr schon im Folgejahr abgerechnet, meist etwa im zweiten Quartal. Die Nachzahlung entsteht, weil die ersten Vorschreibungen nur vorläufig sind.',
  ),
  plainFaq(
    'Wie kann ich meine SVS-Nachzahlung berechnen?',
    'Grob so: Gewinn des Jahres plus die in diesem Jahr vorgeschriebenen Kranken- und Pensionsbeiträge, davon rund 27 %, minus die Beiträge, die dir für dieses Jahr schon vorgeschrieben wurden. In den ersten beiden Jahren als Gewerbetreibende:r wird nur die Pensionsversicherung (18,5 %) nachbemessen. Zähl nur Beiträge für das betreffende Jahr — Zahlungen im selben Jahr können Nachzahlungen für frühere Jahre enthalten. Als Rücklage hat sich rund ein Drittel des Gewinns für Einkommensteuer und SVS gemeinsam bewährt, auf einem eigenen Konto.',
  ),
  plainFaq(
    'Ist die SVS-Nachzahlung steuerlich absetzbar?',
    'Ja, SVS-Beiträge sind Betriebsausgaben — auch Nachzahlungen, und zwar in dem Jahr, in dem du sie bezahlst (bei Einnahmen-Ausgaben-Rechnung). Sie sind auch zusätzlich zur Basispauschalierung oder Kleinunternehmerpauschalierung absetzbar. Eine große Nachzahlung, die du 2028 zahlst, senkt also deine Einkommensteuer für 2028, nicht für das Jahr, für das sie gilt.',
  ),
  {
    q: 'Soll ich SVS-Beiträge vor Jahresende freiwillig vorauszahlen?',
    aPlain:
      'Manchmal — meistens verschiebt eine Vorauszahlung Steuer und SVS aber nur zeitlich, gespart wird selten. Die Zahlung ist im Jahr der Zahlung als Betriebsausgabe absetzbar, wenn sie auf einer sorgfältigen Schätzung beruht, und landet als Guthaben auf deinem SVS-Konto, das die SVS automatisch mit den nächsten Vorschreibungen verrechnet — deine Quartalsvorschreibungen musst du deshalb trotzdem weiter zahlen. Echt lohnen kann sie sich, wenn dein Einkommen heuer deutlich höher ist als in den Folgejahren (z. B. Betriebsende, Karenz, Auslandsaufenthalt); bei einem Gewinn unter rund 20.000 € ist sie selten dringend.',
    a: (
      <>
        Manchmal — meistens verschiebt eine Vorauszahlung Steuer und SVS aber nur zeitlich, gespart wird selten. Die
        Zahlung ist im Jahr der Zahlung als Betriebsausgabe absetzbar, wenn sie auf einer sorgfältigen Schätzung
        beruht, und landet als Guthaben auf deinem SVS-Konto, das die SVS automatisch mit den nächsten Vorschreibungen
        verrechnet — deine Quartalsvorschreibungen musst du deshalb trotzdem weiter zahlen. Echt lohnen kann sie sich,
        wenn dein Einkommen heuer deutlich höher ist als in den Folgejahren (z. B. Betriebsende, Karenz,
        Auslandsaufenthalt); bei einem Gewinn unter rund 20.000 € ist sie selten dringend. Mehr dazu:{' '}
        <Link to={TOPIC.de.shortPath} className="text-pstax-link hover:underline">
          SVS-Vorauszahlung kurz erklärt
        </Link>
        .
      </>
    ),
  },
  plainFaq(
    'Kann ich die SVS-Nachzahlung in Raten zahlen?',
    'Ja. Die Nachzahlung wird ohnehin auf die vier Quartale des Folgejahres verteilt; Nachzahlungen aus den ersten drei Jahren der Selbständigkeit kannst du auf Antrag sogar auf zwölf Quartale strecken. Reicht das nicht, beantrag vor der Fälligkeit Raten oder eine Stundung — das wird in der Regel bewilligt. Lass die Fälligkeit nicht einfach verstreichen, sonst laufen Verzugszinsen.',
  ),
  plainFaq(
    'Kann ich meine SVS-Beiträge herabsetzen?',
    'Ja, wenn dein Gewinn heuer voraussichtlich deutlich unter der vorläufigen Beitragsgrundlage liegt, kannst du bei der SVS eine Herabsetzung beantragen. Das senkt nur die laufenden Vorschreibungen, nicht die endgültigen Beiträge: Ist der Gewinn am Ende höher, kommt die Differenz als Nachzahlung. Unter den Mindestbeitrag kommst du mit Gewerbeschein nicht; ruht der Betrieb wirklich, hilft nur, das Gewerbe ruhend zu melden oder zurückzulegen.',
  ),
  plainFaq(
    'Kann ich mich als Kleinunternehmer:in von der SVS befreien lassen?',
    'Als Gewerbetreibende:r ja, wenn 2026 dein Gewinn höchstens 6.613,20 € und dein Umsatz höchstens 55.000 € beträgt und du zu einer begünstigten Gruppe gehörst — etwa weil du in den letzten fünf Jahren höchstens zwölf Monate nach GSVG pflichtversichert warst, ein bestimmtes Alter erreicht hast oder Kinderbetreuungsgeld beziehst. Die Ausnahme gilt frühestens ab 1. Jänner des Jahres, in dem der Antrag bei der SVS einlangt; ein Antrag bis 31. Dezember kann also noch das laufende Jahr abdecken, wenn du in dem Jahr keine Leistungen bezogen hast. Die Unfallversicherung von 12,95 € im Monat bleibt.',
  ),
  plainFaq(
    'Ich bin angestellt und nebenbei selbständig. Zahle ich doppelt?',
    'Du bekommst zwei Vorschreibungen: ASVG auf dein Gehalt und SVS auf deinen selbständigen Gewinn, und bis zur Höchstbeitragsgrundlage zahlst du auf beides voll. Beide Beitragsgrundlagen zusammen sind aber mit der Höchstbeitragsgrundlage gedeckelt (2026: 8.085 € monatlich, 97.020 € jährlich). Die SVS berücksichtigt das automatisch über eine Differenzvorschreibung und erstattet zu viel gezahlte Pensionsbeiträge von Amts wegen; nur die Unfallversicherung zahlst du doppelt.',
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, öffnet in neuem Tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Bald
  </span>
);

export const SvsSozialversicherungGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_DE,
    description: DESCRIPTION_DE,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'de',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/svs-social-insurance/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/svs-sozialversicherung/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/svs-social-insurance/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_DE,
      breadcrumbJsonLd(CRUMBS, PATH),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_DE.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <Breadcrumb crumbs={CRUMBS} lang="de" />

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Artikel · Sozialversicherung
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-4 leading-tight">
              {H1}
            </h1>
            <QuickLink topic={TOPIC} lang="de" />
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Alle fürchten das Finanzamt. Teuer wird es meistens bei der SVS — weil die ersten Vorschreibungen nur
              vorläufig sind und dir das niemand sagt.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                Von{' '}
                <a href="#autor" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , Steuerberater · Stand: <time dateTime="2026-11-02">November 2026</time>
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="EN" label="This page in English →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">SVS 2026 auf einen Blick</h2>
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {AT_A_GLANCE.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm">
                    <dt className="text-pstax-gray">{label}</dt>
                    <dd className="font-semibold text-pstax-navy text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Calculator promo */}
            <div className="mb-10 bg-pstax-blue/10 border border-pstax-blue/30 rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-black uppercase tracking-wider text-pstax-navy mb-1">
                  Deine Zahlen schätzen
                </p>
                <p className="text-sm text-pstax-gray">
                  Sieh dir an, was Einkommensteuer und SVS heuer ungefähr von deinem Umsatz nehmen.
                </p>
              </div>
              <Link
                to="/de/#calculator"
                className="btn-outline shrink-0 min-h-[44px] flex items-center justify-center whitespace-nowrap"
              >
                Zum Rechner
              </Link>
            </div>

            {/* Table of contents */}
            <nav className="toc-top mb-12 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8" aria-label="Auf dieser Seite">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">
                Auf dieser Seite
              </p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Was ist die SVS? */}
            <h2 id="was" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was ist die SVS?
            </h2>
            <p className="mb-4 leading-relaxed">
              Die SVS ist die Sozialversicherungsanstalt der Selbständigen. Wer angestellt ist, ist bei der ÖGK
              versichert und der Arbeitgeber erledigt alles. Wer selbständig ist, ist bei der SVS versichert — und
              erledigt alles selbst.
            </p>
            <p className="mb-4 leading-relaxed">
              Die SVS ist <strong>keine Steuer</strong>. Sie ist eine Versicherung und umfasst Kranken-, Pensions-
              und Unfallversicherung sowie die Selbständigenvorsorge.
            </p>
            <p className="mb-4 leading-relaxed">
              Zwei Behörden, zwei Vorschreibungen: Das <strong>Finanzamt</strong> holt sich die Einkommensteuer, die{' '}
              <strong>SVS</strong> die Sozialversicherung. Die beiden reden miteinander — und genau daraus entsteht
              die Überraschung. Dazu weiter unten mehr.
            </p>

            {/* Muss ich zahlen */}
            <h2 id="wer" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Muss ich überhaupt SVS zahlen?
            </h2>
            <p className="mb-4 leading-relaxed">
              Mit hoher Wahrscheinlichkeit ja. Welche Regeln für dich gelten, hängt davon ab, <em>wie</em> du
              selbständig bist. Es gibt zwei Gruppen.
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              1. Mit Gewerbeschein (Gewerbetreibende)
            </h3>
            <p className="mb-4 leading-relaxed">
              Wenn deine Tätigkeit einen Gewerbeschein braucht — IT-Dienstleistungen, Fotografie, die meisten
              Beratungsgewerbe, Personal Training, viele Handwerke — wirst du mit der Anmeldung Mitglied der
              Wirtschaftskammer. Die SVS-Pflichtversicherung beginnt am selben Tag.{' '}
              <strong>Eine Einkommensgrenze gibt es grundsätzlich nicht:</strong> Auch bei null Umsatz fällt der
              Mindestbeitrag an. Unter bestimmten Voraussetzungen kannst du dich aber auf Antrag von einem Großteil
              der Beiträge befreien lassen (mehr dazu unter{' '}
              <a href="#kleinstunternehmerregelung" className="text-pstax-link hover:underline">
                Die Kleinstunternehmerregelung
              </a>
              ).
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              2. Ohne Gewerbeschein (Neue Selbständige)
            </h3>
            <p className="mb-4 leading-relaxed">
              Manche Tätigkeiten brauchen keinen Gewerbeschein — Schreiben, Vortragen, Übersetzen, bestimmte
              therapeutische und künstlerische Arbeit. Das sind die sogenannten Neuen Selbständigen. Hier gilt eine
              Grenze: Pflichtversichert bist du erst, wenn dein <strong>Gewinn 2026 über 6.613,20 €</strong> liegt.
            </p>
            <Callout tone="blue" label="Wichtig">
              In welche Gruppe du fällst, ist keine Entscheidung. Es ergibt sich aus der Tätigkeit selbst. Das am
              Anfang falsch einzuordnen gehört zu den teureren Fehlern, weil es rückwirkend korrigiert wird.
            </Callout>
            <p className="mb-4 leading-relaxed">
              Du bist angestellt <strong>und</strong> nebenbei selbständig? Dann zahlst du ASVG über den Dienstgeber
              und SVS auf deinen selbständigen Gewinn. Verloren ist dabei nichts: Die beiden Beitragsgrundlagen
              zusammen sind mit der Höchstbeitragsgrundlage gedeckelt. Die SVS berücksichtigt das automatisch
              (Differenzvorschreibung) und erstattet zu viel gezahlte Pensionsbeiträge von Amts wegen — nur die
              Unfallversicherung zahlst du doppelt.
            </p>

            {/* Wie viel kostet */}
            <h2 id="kosten" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wie viel kostet die SVS 2026?
            </h2>
            <p className="mb-6 leading-relaxed">
              Die Sätze sind Prozentsätze deiner <strong>Beitragsgrundlage</strong>. Alle zahlen mindestens auf die
              Mindestbeitragsgrundlage, niemand auf mehr als die Höchstbeitragsgrundlage.
            </p>

            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm border-collapse">
                <caption className="text-left text-xs text-pstax-gray mb-3">
                  SVS-Sätze 2026 und der Beitrag auf die Mindestbeitragsgrundlage
                </caption>
                <thead>
                  <tr className="border-b-2 border-pstax-navy">
                    <th scope="col" className="text-left py-2 pr-4 font-bold text-pstax-navy">
                      Sparte
                    </th>
                    <th scope="col" className="text-right py-2 pr-4 font-bold text-pstax-navy">
                      Satz 2026
                    </th>
                    <th scope="col" className="text-right py-2 font-bold text-pstax-navy">
                      Auf 551,10 € / Monat
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Pensionsversicherung
                    </th>
                    <td className="text-right py-2 pr-4">18,50 %</td>
                    <td className="text-right py-2">101,95 €</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Krankenversicherung
                    </th>
                    <td className="text-right py-2 pr-4">6,80 %</td>
                    <td className="text-right py-2">37,48 €</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Selbständigenvorsorge
                    </th>
                    <td className="text-right py-2 pr-4">1,53 %</td>
                    <td className="text-right py-2">8,43 €</td>
                  </tr>
                  <tr className="border-b border-pstax-border">
                    <th scope="row" className="text-left py-2 pr-4 font-normal">
                      Unfallversicherung
                    </th>
                    <td className="text-right py-2 pr-4">Fixbetrag</td>
                    <td className="text-right py-2">12,95 €</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="font-bold text-pstax-navy">
                    <th scope="row" className="text-left py-2 pr-4">
                      Mindestbeitrag gesamt
                    </th>
                    <td className="text-right py-2 pr-4">—</td>
                    <td className="text-right py-2">160,81 € / Monat</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-sm text-pstax-gray mb-6 leading-relaxed">
              Das sind <strong>482,43 € pro Quartal</strong> oder <strong>1.929,72 € pro Jahr</strong> — weniger
              zahlt 2026 niemand. Die Höchstbeitragsgrundlage liegt bei 8.085,00 € monatlich (97.020,00 € jährlich);
              darüber steigen die Beiträge nicht weiter. Sätze und Grundlagen veröffentlicht die Wirtschaftskammer,{' '}
              <ExternalLink
                href="https://www.wko.at/entlohnung/beitragswesen-selbstaendige-2026"
                className="text-pstax-link hover:underline"
              >
                Beitragswesen Selbständige 2026
              </ExternalLink>
              .
            </p>

            <Callout tone="orange" label="Wird gern übersehen">
              Deine Beitragsgrundlage ist <strong>nicht</strong> einfach dein Gewinn. Sie ist dein Gewinn{' '}
              <strong>plus</strong> die Kranken- und Pensionsbeiträge, die dir die SVS in diesem Jahr vorgeschrieben
              hat — inklusive Nachzahlungen für frühere Jahre, die in diesem Jahr vorgeschrieben wurden. Die Grundlage liegt also immer etwas über der Zahl in deiner Steuererklärung — und die
              Vorschreibung entsprechend auch.
            </Callout>

            <p className="mb-4 leading-relaxed">
              Du willst eine Hausnummer für deine Situation? Unser{' '}
              <Link to="/de/#calculator" className="text-pstax-link hover:underline font-semibold">
                Steuer-Rechner
              </Link>{' '}
              schätzt aus Umsatz und Ausgaben, was von Einkommensteuer und SVS übrig bleibt.
            </p>

            {/* Erste Jahre */}
            <h2 id="erste-jahre" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Warum das erste Jahr günstig wirkt — und was daran das Problem ist
            </h2>
            <p className="mb-4 leading-relaxed">
              In den ersten drei Kalenderjahren deiner Selbständigkeit schreibt dir die SVS vorläufig den Mindestbeitrag
              vor — 160,81 € im Monat, egal was du verdienst. Das fühlt sich an wie ein Geschenk. Es ist eher ein Kredit.
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Krankenversicherung, Jahr 1 und 2 (mit Gewerbeschein):</strong> fix auf dem Mindestbeitrag,
                und sie wird <em>nie</em> nachbemessen — sofern du in den zehn Jahren davor nicht nach GSVG versichert
                warst. Dasselbe gilt für die Selbständigenvorsorge. Dieser Teil ist tatsächlich ein Geschenk.
              </li>
              <li>
                <strong>Pensionsversicherung, ab dem ersten Tag:</strong> vorläufig. Sie wird immer nachbemessen, für
                jedes Jahr.
              </li>
              <li>
                <strong>Ab dem dritten Jahr:</strong> auch die Krankenversicherung wird nachbemessen.
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Wenn du also im ersten Jahr 50.000 € verdienst und 1.929,72 € zahlst, hast du nicht 1.929,72 € für
              dieses Jahr gezahlt. Du hast eine Anzahlung geleistet.
            </p>

            {/* Nachbemessung */}
            <h2 id="nachzahlung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              SVS-Nachzahlung: wann sie kommt und wie du sie berechnest
            </h2>
            <p className="mb-6 leading-relaxed">
              Die SVS-Nachzahlung entsteht durch die <strong>Nachbemessung</strong> — das Wichtigste, was man über die
              SVS verstehen muss. Sie läuft in drei Schritten ab.
            </p>

            <ol className="mb-8 space-y-4">
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  1
                </span>
                <p className="leading-relaxed">
                  <strong>Du zahlst einen vorläufigen Betrag</strong> — Die SVS kennt deinen Gewinn dieses Jahres
                  noch nicht. Also schreibt sie vorläufig vor: in den ersten drei Jahren den Mindestbeitrag, ab dem
                  vierten Jahr deine Beitragsgrundlage aus dem drittvorangegangenen Jahr, aufgewertet.
                </p>
              </li>
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  2
                </span>
                <p className="leading-relaxed">
                  <strong>Dein Steuerbescheid kommt</strong> — Du gibst die Einkommensteuererklärung ab, das
                  Finanzamt erlässt den Einkommensteuerbescheid und übermittelt die Zahlen an die SVS. Den Bescheid
                  für ein Jahr bekommst du typischerweise im Jahr danach.
                </p>
              </li>
              <li className="bg-white border border-pstax-border rounded-2xl p-5 flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-pstax-navy text-white font-bold flex items-center justify-center">
                  3
                </span>
                <p className="leading-relaxed">
                  <strong>Die SVS rechnet neu — und belastet doppelt</strong> — Die Nachzahlung für das alte Jahr
                  schreibt die SVS im darauffolgenden Jahr vor, verteilt auf die vier Quartale. Ohne dein Zutun
                  zahlst du für Jahr 1 also erst in Jahr 3. Ab dem vierten Jahr richtet sich zusätzlich auch deine{' '}
                  <em>laufende</em> Vorschreibung nach deinem tatsächlichen Gewinn — dann zahlst du eine Zeit lang
                  Nachzahlung und höhere laufende Beiträge gleichzeitig.
                </p>
              </li>
            </ol>

            <div className="bg-pstax-navy text-white rounded-2xl p-6 sm:p-8 mb-8">
              <h3 className="text-lg font-display font-bold mb-1">Was das in Zahlen heißt</h3>
              <p className="text-sm text-white/70 mb-5">
                Vereinfachtes Beispiel: erstes Jahr der Selbständigkeit 2026 mit Gewerbeschein, Gewinn 45.000 €, keine weiteren
                Einkünfte.
              </p>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>2026 an die SVS gezahlt</dt>
                  <dd className="font-semibold">1.929,72 €</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>Nachzahlung für 2026 (Bescheid 2027, vorgeschrieben 2028 in vier Quartalen)</dt>
                  <dd className="font-bold text-pstax-orange">≈ 7.400 €</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/15 pb-3">
                  <dt>Monatsbeitrag vor der Nachbemessung</dt>
                  <dd className="font-semibold">160,81 €</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Laufender Monatsbeitrag ab 2029 (vorläufig, auf Basis 2026)</dt>
                  <dd className="font-bold text-pstax-orange">≈ 1.050 €</dd>
                </div>
              </dl>
              <p className="text-xs text-white/60 mt-5 leading-relaxed">
                Gerundete Darstellung auf Basis der Sätze von 2026, für einen konkreten Fall. Deine Zahlen sehen
                anders aus. Mit Gewerbeschein werden Kranken- und Selbständigenvorsorge im ersten Jahr nicht
                nachbemessen, nachgezahlt wird nur die Pensionsversicherung. Der Monatsbeitrag ab 2029 ist ohne die
                jährliche Aufwertung gerechnet; die Nachzahlung für 2027 kommt 2029 noch dazu.
              </p>
            </div>

            <p className="mb-4 leading-relaxed">
              In diesem Beispiel ist nichts schiefgegangen. Niemand hat einen Fehler gemacht. So funktioniert das
              System — und deshalb sollte man als Selbständige:r in Österreich immer Geld halten, das sich nicht
              nach dem eigenen anfühlt.
            </p>
            <p className="mb-4 leading-relaxed">
              Du musst aber nicht bis Jahr 3 warten. Sobald der Einkommensteuerbescheid da ist, kann man bei der SVS
              eine <strong>vorzeitige Endabrechnung</strong> beantragen. Dann ist Jahr 1 schon etwa im zweiten Quartal
              von Jahr 2 abgerechnet — die Nachzahlung kommt früher, aber planbar und ohne dass sich mehrere Jahre
              übereinanderschieben. Für unsere Klient:innen beantragen wir das nach dem Bescheid (mit ihrem OK).
            </p>

            <SvsVorschreibungMock
              legendTitle="Was die vier Markierungen zeigen"
              legend={[
                <>Nur ein Quartal. Drei weitere Vorschreibungen folgen im selben Jahr.</>,
                <>Die Beitragsgrundlage — jeder Betrag darunter ist ein Prozentsatz dieser einen Zahl.</>,
                <>Was du tatsächlich überweist. Es fühlt sich an wie der Preis. Es ist eine Anzahlung.</>,
                <>
                  <strong>Die Zeile, die fast niemand liest.</strong> „Vorläufig" heißt: Dieser Betrag wird neu
                  berechnet, sobald dein Steuerbescheid da ist.
                </>,
              ]}
            />
            <p className="text-sm text-pstax-gray -mt-6 mb-10 leading-relaxed">
              Nachgebaute SVS-Quartalsvorschreibung, mit den Mindestwerten 2026 — kein Scan eines echten Dokuments.
              Die entscheidende Zeile ist die, die die meisten überspringen.
            </p>

            <h3 id="nachzahlung-berechnen" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              So berechnest du deine SVS-Nachzahlung — grob, in vier Schritten
            </h3>
            <ol className="mb-4 space-y-2 list-decimal pl-6 leading-relaxed">
              <li>
                <strong>Gewinn des Jahres nehmen</strong> — aus deiner Buchhaltung oder Steuererklärung.
              </li>
              <li>
                <strong>Davon rund 27 % rechnen.</strong> Das ist ungefähr die SVS, die für dieses Jahr insgesamt
                anfällt. Genauer: Gewinn plus die für dieses Jahr vorgeschriebenen Kranken- und Pensionsbeiträge, davon
                27 %. In den ersten beiden Jahren mit Gewerbeschein wird nur die Pensionsversicherung nachbemessen —
                dann rechnest du mit 18,5 %.
              </li>
              <li>
                <strong>Abziehen, was dir für dieses Jahr schon vorgeschrieben wurde.</strong> Nur Beiträge{' '}
                <em>für</em> dieses Jahr zählen — was du heuer gezahlt hast, kann Nachzahlungen für frühere Jahre
                enthalten.
              </li>
              <li>
                <strong>Der Rest ist deine ungefähre Nachzahlung.</strong> Im Beispiel oben: (45.000 € + 1.673 €
                vorgeschriebene Beiträge) × 18,5 % − 1.223 € bereits vorgeschriebene Pensionsbeiträge ≈ 7.400 €.
              </li>
            </ol>
            <p className="mb-4 leading-relaxed">
              Noch gröber, wenn du deinen Gewinn nicht kennst: Mit Basis- oder Kleinunternehmerpauschalierung liegt
              die SVS bei rund <strong>20 % deines Umsatzes</strong> — davon ziehst du wieder ab, was für dieses Jahr
              schon vorgeschrieben wurde. Eine schnelle Schätzung gibt dir der Rechner unten; Einkommensteuer und
              SVS gemeinsam schätzt unser{' '}
              <Link to="/de/#calculator" className="text-pstax-link hover:underline font-semibold">
                Steuer-Rechner
              </Link>
              .
            </p>
            <SvsEstimateCalculator lang="de" />

            {/* Wann zahlst du */}
            <h2 id="wann" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wann zahlst du?
            </h2>
            <p className="mb-4 leading-relaxed">
              Die SVS schreibt quartalsweise vor. Die Fälligkeiten sind immer dieselben:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>28. Februar</strong> — erstes Quartal
              </li>
              <li>
                <strong>31. Mai</strong> — zweites Quartal
              </li>
              <li>
                <strong>31. August</strong> — drittes Quartal
              </li>
              <li>
                <strong>30. November</strong> — viertes Quartal
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Du kannst auf monatliche Abbuchung umstellen, was den meisten unserer Klient:innen die Planung
              erleichtert. Nachzahlungen aus der Nachbemessung kommen zusätzlich, verteilt auf die vier
              Quartalsvorschreibungen des Folgejahres. Wer zu spät zahlt, zahlt Verzugszinsen — wenn es eng wird, also lieber <em>vor</em> der
              Fälligkeit um Ratenzahlung ansuchen, statt es laufen zu lassen.
            </p>

            {/* Leistungen */}
            <h2 id="leistungen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wofür zahlst du eigentlich?
            </h2>
            <p className="mb-4 leading-relaxed">
              Eine Rechnung, die man versteht, zahlt sich leichter. Deine SVS-Beiträge bringen dir:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Krankenversicherung</strong> — e-card, Arzt und Facharzt, Spital, und Mitversicherung von
                Angehörigen.
              </li>
              <li>
                <strong>Pensionszeiten</strong> — mit Abstand der größte Teil der Vorschreibung. Jedes Versicherungsjahr zählt für deine Pension und für die
                Mindestversicherungszeiten, die du überhaupt brauchst, um eine zu bekommen.
              </li>
              <li>
                <strong>Unfallversicherung</strong> — auch auf dem Weg zum Kunden, für 12,95 € im Monat.
              </li>
              <li>
                <strong>Selbständigenvorsorge</strong> — 1,53 % wandern in einen Topf auf deinen Namen. Der gehört
                dir und wird später ausgezahlt.
              </li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Dazu kommen Leistungen, von denen viele nichts wissen: Unterstützung bei längerer Krankheit,
              Wochengeld, Vorsorgeprogramme. Was davon auf dich zutrifft, hängt von deiner Situation ab — fragen
              lohnt sich mehr als annehmen.
            </p>

            {/* Geringes Einkommen */}
            <h2 id="geringes-einkommen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Wenn du wenig verdienst
            </h2>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Unter der Versicherungsgrenze als Neue:r Selbständige:r
            </h3>
            <p className="mb-4 leading-relaxed">
              Bleibst du 2026 unter 6.613,20 € Gewinn, gibt es keine Pflichtversicherung in Kranken- und
              Pensionsversicherung. Daran hängt aber eine Falle:
            </p>
            <Callout tone="orange" label="Der Beitragszuschlag von 9,3 %">
              Wenn dein Gewinn die Grenze doch überschreitet, musst du das der SVS selbst melden — mit einer{' '}
              <strong>Überschreitungserklärung</strong>, entweder im laufenden Jahr oder innerhalb von{' '}
              <strong>acht Wochen</strong> nach deinem Steuerbescheid. Versäumst du das, verrechnet die SVS einen
              Zuschlag von <strong>9,3 % auf die Beiträge</strong>. Es ist eine der am leichtesten vermeidbaren
              Strafzahlungen im österreichischen System — und eine der am häufigsten gezahlten.
            </Callout>
            <h3 id="kleinstunternehmerregelung" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Die Kleinstunternehmerregelung
            </h3>
            <p className="mb-4 leading-relaxed">
              Wenn du einen Gewerbeschein hast, dein Betrieb aber wirklich klein ist, kannst du dich von der Kranken-
              und Pensionsversicherung ausnehmen lassen. Für 2026 müssen beide Grenzen halten:{' '}
              <strong>Gewinn höchstens 6.613,20 €</strong> und <strong>Umsatz höchstens 55.000 €</strong>.
            </p>
            <p className="mb-4 leading-relaxed">
              Zwei Dinge werden hier oft falsch verstanden. Erstens steht die Ausnahme nur bestimmten Gruppen offen —
              vor allem Personen, die in den letzten fünf Jahren höchstens zwölf Monate nach GSVG pflichtversichert
              waren (also Neugründer:innen), älteren Selbständigen (ab 57 unter weiteren Voraussetzungen) und Eltern,
              die Kinderbetreuungsgeld beziehen. Zweitens beginnt sie{' '}
              <strong>frühestens mit 1. Jänner des Jahres, in dem dein Antrag bei der SVS einlangt</strong> — ein
              Antrag bis 31. Dezember kann also noch das laufende Jahr abdecken (wenn du in diesem Jahr keine
              Leistungen bezogen hast), für Vorjahre geht es nicht mehr. Die Unfallversicherung von 12,95 € im Monat
              bleibt in jedem Fall verpflichtend.
            </p>

            {/* Zu hoch */}
            <h2 id="zu-hoch" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Die Vorschreibung ist zu hoch — oder zu niedrig. Was du tun kannst
            </h2>
            <p className="mb-4 leading-relaxed">
              Mehr als die meisten denken. Die SVS ist deutlich flexibler, als ihre Schreiben klingen — nur passiert
              nichts von selbst.
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Die vorläufigen Beiträge herabsetzen (Herabsetzungsantrag)
            </h3>
            <p className="mb-4 leading-relaxed">
              Wenn dieses Jahr klar schwächer wird als das Vergleichsjahr, kannst du eine Herabsetzung der
              vorläufigen Beiträge beantragen. Praktisch, wenn es liquiditätsmäßig eng ist — aber sei dir im Klaren,
              was es bedeutet: weniger jetzt heißt mehr bei der Nachbemessung.
            </p>
            <p className="mb-4 leading-relaxed">
              Umgekehrt geht es auch: Beantragst du im SVS-Portal eine <strong>Hinaufsetzung</strong> (Erhöhung) der
              vorläufigen Beitragsgrundlage, siehst du dort den neuen, höheren Quartalsbetrag — und die SVS verrechnet
              die Differenz für <strong>alle bereits vergangenen Quartale des laufenden Jahres sofort nach</strong>. Du
              zahlst dann auf einen Schlag einen größeren Betrag. Das kann sinnvoll sein; im vierten Quartal, ohne das
              zu wissen, ist es aber riskant. Eine freiwillige Vorauszahlung ändert die Vorschreibung dagegen nicht.
            </p>
            <h3 id="jahresende" className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Freiwillig vor dem 31. Dezember vorauszahlen
            </h3>
            <p className="mb-4 leading-relaxed">
              Du kannst vor Jahresende freiwillig einen Betrag an die SVS überweisen, der ungefähr der zu erwartenden
              Nachzahlung für das laufende Jahr entspricht. Das passiert dabei:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>
                Die Zahlung ist <strong>im Jahr der Zahlung als Betriebsausgabe absetzbar</strong>, wenn sie auf
                einer sorgfältigen Schätzung beruht (EStR Rz 4623) — auch zusätzlich zur Basis- oder
                Kleinunternehmerpauschalierung.
              </li>
              <li>
                Sie landet als <strong>Guthaben</strong> auf deinem SVS-Konto. Zweckwidmen kannst du es nicht: Die
                SVS verrechnet es automatisch mit den nächsten Quartalsvorschreibungen.
              </li>
              <li>
                Die Vorschreibung selbst ändert sich nicht — anders als bei einer Hinaufsetzung, bei der die SVS die
                Differenz für die schon vergangenen Quartale des Jahres sofort auf einmal nachverrechnet. Die Grundlage
                steigt erst, wenn die SVS mit dem Steuerbescheid endgültig abrechnet.
              </li>
            </ul>
            <Callout tone="orange" label="Wichtig">
              Zahl deine Quartalsvorschreibungen nach einer Vorauszahlung <strong>ganz normal weiter</strong>. Sonst
              ist das Guthaben aufgebraucht, bevor die Nachzahlung kommt. Eine Rückzahlung des Guthabens wäre
              übrigens wieder eine Betriebseinnahme — davon raten wir ab.
            </Callout>
            <p className="mb-4 leading-relaxed">
              Ehrlich gesagt: In den meisten Fällen ist die Vorauszahlung eine <strong>Verschiebung, keine
              Ersparnis</strong> — bei der Steuer wie bei der SVS. Die Beiträge zählen im Jahr ihrer Vorschreibung zur
              Beitragsgrundlage (§ 25 GSVG), und die Betriebsausgabe fällt nur früher an. Einen echten Steuervorteil
              gibt es, wenn du im Jahr mit dem höheren Einkommen und Grenzsteuersatz zahlst — etwa wenn du den
              Betrieb beendest oder pausierst, nächstes Jahr deutlich weniger verdienst, in Karenz gehst oder ins
              Ausland ziehst.
            </p>
            <p className="mb-4 leading-relaxed">
              Der eigentliche Nutzen ist ein anderer: Du weißt, was kommt, und wirst nicht überrascht.
            </p>
            <p className="mb-4 leading-relaxed">
              Eher nicht sinnvoll ist sie, wenn du gerade erst begonnen hast oder sehr wenig Umsatz machst (unter rund
              10.000 € Umsatz bzw. 6.613,20 € Gewinn), wenn du später deutlich höhere Gewinne erwartest, wenn dein
              Gewinn heuer ohnehin durch große Nachzahlungen für Vorjahre niedrig ist, oder wenn du dir die Zahlung
              schlicht nicht leisten kannst. Unter rund 20.000 € Gewinn (Grenzsteuersatz höchstens 20 %) ist sie
              selten dringend. Achtung beim Vergleich: Was du heuer an die SVS gezahlt hast, kann Nachzahlungen für
              frühere Jahre enthalten — zählen nur die Beiträge <em>für</em> das laufende Jahr.
            </p>
            <p className="mb-4 leading-relaxed">
              So machen wir das mit unseren Klient:innen: Ihr haltet die Buchhaltung aktuell. Ab Anfang November,
              sobald die Vorschreibung für das vierte Quartal im SVS-Portal sichtbar ist, holen wir die SVS-Daten und
              schicken jeder und jedem eine individuelle Schätzung samt Zahlungsinfo (SVS-IBAN,
              Sozialversicherungsnummer, Betrag). Wer bis Mitte Dezember nichts von uns gehört hat, muss nichts tun.
              Die Kurzfassung für Klient:innen:{' '}
              <Link to={TOPIC.de.shortPath} className="text-pstax-link hover:underline font-semibold">
                SVS-Vorauszahlung kurz erklärt
              </Link>
              .
            </p>
            <h3 className="text-xl font-display font-bold text-pstax-navy mt-6 mb-3">
              Ratenzahlung und Stundung
            </h3>
            <p className="mb-4 leading-relaxed">
              Kommt eine Nachzahlung, die du nicht stemmen kannst, bewilligt die SVS in aller Regel Raten. Du musst
              nur fragen, schriftlich, und am besten vor der Fälligkeit. Nachzahlungen aus den ersten drei Jahren der
              Selbständigkeit kannst du auf Antrag außerdem statt auf vier auf zwölf Quartale verteilen.
            </p>

            {/* Steuer */}
            <h2 id="steuer" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              SVS und Steuer gehören zusammen
            </h2>
            <p className="mb-4 leading-relaxed">Die beiden werden oft als getrennte Probleme behandelt. Sind sie nicht.</p>
            <p className="mb-4 leading-relaxed">
              SVS-Beiträge sind Betriebsausgaben — auch neben der Basis- oder Kleinunternehmerpauschalierung. Sie
              senken deinen Gewinn und damit deine Einkommensteuer, und zwar (bei Einnahmen-Ausgaben-Rechnung) im Jahr
              der Zahlung. Das hat praktische Folgen:
            </p>
            <ul className="mb-4 space-y-2 list-disc pl-6 leading-relaxed">
              <li>Eine große SVS-Nachzahlung 2028 senkt deine Steuer für 2028.</li>
              <li>Ob du am 28. Dezember oder am 2. Jänner zahlst, verschiebt die Absetzbarkeit um ein ganzes Jahr.</li>
              <li>Setzt du deine SVS-Beiträge herab, steigt dein steuerpflichtiger Gewinn im selben Jahr.</li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Wer beides getrennt plant, zahlt fast immer mehr als nötig. Siehe dazu auch unseren Beitrag zur
              Einkommensteuer für Selbständige.
              <SoonBadge />
            </p>

            {/* Fehler */}
            <h2 id="fehler" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Fünf Fehler, die wir am häufigsten sehen
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                <strong>Die ersten Vorschreibungen für die echten Kosten halten.</strong> Das ist eine Anzahlung,
                kein Preis.
              </li>
              <li>
                <strong>Nichts zur Seite legen.</strong> Als Faustregel: rund ein Drittel des Gewinns für Steuer und
                SVS gemeinsam, auf ein eigenes Konto.
              </li>
              <li>
                <strong>Die Überschreitungserklärung versäumen.</strong> Acht Wochen nach dem Bescheid, dann 9,3 %
                obendrauf.
              </li>
              <li>
                <strong>Die Beiträge herabsetzen, ohne zu planen.</strong> Das verschiebt das Problem, es löst es
                nicht.
              </li>
              <li>
                <strong>Post liegen lassen.</strong> SVS-Schreiben enthalten Fristen, die mit der Zustellung zu
                laufen beginnen.
              </li>
            </ul>

            <figure className="mb-8">
              <picture>
                <source srcSet="/pstax-team-vienna.webp" type="image/webp" />
                <img
                  src="/pstax-team-vienna.jpg"
                  alt="Das PSTax-Team in Wien, gemeinsam bei der Arbeit an einem Klientenfall an einem gemeinsamen Schreibtisch."
                  width={1400}
                  height={934}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-2xl border border-pstax-border"
                />
              </picture>
            </figure>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Häufige Fragen
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_DE.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            {/* Quellen */}
            <h2 id="quellen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Offizielle Quellen
            </h2>
            <p className="mb-4 leading-relaxed">
              Alle Zahlen auf dieser Seite stammen aus offiziellen Quellen.
            </p>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                <ExternalLink
                  href="https://www.wko.at/entlohnung/beitragswesen-selbstaendige-2026"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Beitragswesen Selbständige 2026
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Sätze, Mindest- und Höchstbeitragsgrundlage für 2026.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/entlohnung/gewerbliche-sozialversicherung-fuer-neugruender-2026"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Gewerbliche Sozialversicherung für Neugründer 2026
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Die feste Beitragsgrundlage in den ersten Jahren, und was nachbemessen wird und was nicht.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.wko.at/sozialversicherung/gewerbliche-sozialversicherungsbeitraege-grundlage"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  WKO — Endgültige Beitragsgrundlage
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Wie die endgültige Beitragsgrundlage ermittelt wird und wie die Nachbemessung funktioniert.
                </span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/cdscontent/?contentid=10007.816635"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Endgültige Berechnung in den ersten drei Jahren
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">Was in den ersten Jahren nachbemessen wird und wie die Nachzahlung auf Quartale verteilt wird.</span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/cdscontent/?contentid=10007.846813"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Kleinunternehmer
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">Grenzen, begünstigte Gruppen und ab wann die Ausnahme gilt.</span>
              </li>
              <li>
                <ExternalLink
                  href="https://www.svs.at/"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  SVS — Sozialversicherungsanstalt der Selbständigen
                </ExternalLink>
                <br />
                <span className="text-sm text-pstax-gray">
                  Formulare, Fristen, Portal und die Regeln zur Versicherungsgrenze.
                </span>
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Nicht sicher, wo du bei der SVS stehst?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Buch dir ein kostenloses 30-Minuten-Gespräch. Wir schauen uns deine konkrete Situation an, sagen dir,
                was auf dich zukommt, und du entscheidest danach, ob du mit uns arbeiten willst. Ohne Verpflichtung.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/de/#fit-check" className="btn-accent">
                  Gratis Erstgespräch buchen
                </Link>
                <Link to="/de/#calculator" className="btn-outline !border-white !text-white hover:!bg-white/10">
                  Zum Steuer-Rechner
                </Link>
              </div>
              <p className="text-xs text-white/50">
                Fixpreise ab 800 € pro Jahr. Über 600 Klient:innen in Österreich. Kommunikation auf Deutsch oder
                Englisch — wie du willst.
              </p>
            </div>

            {/* Autorenbox */}
            <div id="autor" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, österreichischer Steuerberater und Gründer von PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">
                Fachlich geprüft von Pascal Schraml
              </p>
              <p className="text-sm text-pstax-gray mb-3">Steuerberater, Gründer von PSTax</p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Pascal berät seit 2023 Selbständige in Österreich und prüft jeden Beitrag auf dieser Seite fachlich.
                PSTax betreut über 600 Klient:innen, mehr als die Hälfte davon ohne deutsche
                Muttersprache.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                <strong className="text-pstax-navy">Zu dieser Seite.</strong> Alle Werte gelten für das
                Beitragsjahr 2026 und ändern sich jährlich. Dieser Beitrag gibt allgemeine Informationen zur
                Sozialversicherung der Selbständigen in Österreich; er ist keine individuelle Beratung und kann sie
                nicht ersetzen, weil die richtige Antwort von deiner konkreten Situation abhängt. Fachlich geprüft
                von Pascal Schraml, Steuerberater, PSTax Steuerberatung FlexCo.
              </p>
            </div>

            <MoreTopics currentKey={TOPIC.key} lang="de" />
            <FitCheckBox lang="de" />
          </article>
      </div>
    </div>
  );
};
